import { useState, useCallback, useEffect, useRef } from 'react'
import type { DownloadState, Quality, DownloadProgress } from '../types'
import { bulkDownloadVideos, BulkDownloadResult } from '../services/api'

interface UseBulkDownloadReturn {
  state: DownloadState
  progress: number
  result: BulkDownloadResult | null
  errorMessage: string | null
  urlCount: number
  download: (urls: string[], quality: Quality) => Promise<void>
  reset: () => void
  saveZip: () => void
}

export function useBulkDownload(): UseBulkDownloadReturn {
  const [state, setState] = useState<DownloadState>('idle')
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState<BulkDownloadResult | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [urlCount, setUrlCount] = useState(0)

  const objectUrlRef = useRef<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current)
      }
      abortControllerRef.current?.abort()
    }
  }, [])

  const reset = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null

    setState('idle')
    setProgress(0)
    setErrorMessage(null)
    setUrlCount(0)

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
    setResult(null)
  }, [])

  const download = useCallback(async (urls: string[], quality: Quality) => {
    reset()
    setState('preparing')
    setUrlCount(urls.length)

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const handleProgress = (p: DownloadProgress) => {
        if (p.percent > 0) {
          setState('downloading')
        }
        setProgress(p.percent)
      }

      const downloadResult = await bulkDownloadVideos(
        urls.slice(0, 10), // Limit to 10 (matches backend)
        quality,
        handleProgress,
        controller.signal
      )

      if (controller.signal.aborted) {
        return
      }

      setResult(downloadResult)
      setState('complete')
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }

      const message = err instanceof Error ? err.message : 'Bulk download failed'
      setErrorMessage(message)
      setProgress(0)
      setState('error')
    }
  }, [reset])

  const saveZip = useCallback(() => {
    if (!result) return

    const url = URL.createObjectURL(result.blob)
    const a = document.createElement('a')
    a.href = url
    a.download = result.filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }, [result])

  return {
    state,
    progress,
    result,
    errorMessage,
    urlCount,
    download,
    reset,
    saveZip,
  }
}
